import {
  BOOKING_REORDERING_COHORT,
  type BookingReorderingCohort,
  type PlacementCandidate,
  PlacementCandidateScreeningStatus,
} from "@src/appV2/redesign/PlacementCandidate/types";

interface UseBookingCohortEligibilityParams {
  bookingReorderingCohort: BookingReorderingCohort;
  placementCandidate: Pick<PlacementCandidate, "screeningStatus">;
  isRecruiterScreeningEnabled: boolean;
  /** Whether job interviews feature is enabled */
  isJobInterviewsEnabled?: boolean;
  /** Whether the placement requires an interview */
  requiredInterview?: boolean;
  /** Whether the placement has open house */
  hasOpenHouse?: boolean;
  /** Whether there are missing documents other than the screening document */
  hasNonScreeningDocumentsMissing?: boolean;
}

/**
 * Determines eligibility for booking cohort modals and document bypass conditions.
 * Checks if worker should see screen_last screening modal or book_first confirmation,
 * and whether they can bypass document requirements for the screen_last flow.
 */
export function useBookingCohortEligibility(params: UseBookingCohortEligibilityParams) {
  const {
    bookingReorderingCohort,
    placementCandidate,
    isRecruiterScreeningEnabled,
    isJobInterviewsEnabled = false,
    requiredInterview = false,
    hasOpenHouse = false,
    hasNonScreeningDocumentsMissing = false,
  } = params;

  const hasNotPassedScreening = !placementCandidate.screeningStatus?.startsWith(
    PlacementCandidateScreeningStatus.PASSED
  );
  const hasNotFailedScreening = !placementCandidate.screeningStatus?.startsWith(
    PlacementCandidateScreeningStatus.FAILED
  );
  const isNotPendingScreening =
    placementCandidate.screeningStatus !== PlacementCandidateScreeningStatus.PENDING;

  const isScreenLastStepCohort = bookingReorderingCohort === BOOKING_REORDERING_COHORT.SCREEN_LAST;
  const hasInterviewBookingFlow = (isJobInterviewsEnabled && requiredInterview) || hasOpenHouse;

  // Check if eligible to show screen_last screening modal (excludes runtime modal type check)
  // Note: showCallMeNow is not checked here - the modal should still show outside of call hours
  // with just the Calendly scheduling option available
  const isEligibleForScreenLastScreeningModal =
    isScreenLastStepCohort &&
    hasNotPassedScreening &&
    isNotPendingScreening &&
    isRecruiterScreeningEnabled;

  // Check if should show BookFirstInterviewConfirmationDialog instead of InterviewBookedDialog
  const isEligibleForBookFirstConfirmation =
    bookingReorderingCohort === BOOKING_REORDERING_COHORT.BOOK_FIRST &&
    hasNotPassedScreening &&
    isRecruiterScreeningEnabled;

  // Allow screen_last cohort to bypass the screening document when they haven't been screened yet.
  // This enables the "book first, screen later" flow for this cohort.
  // Only bypasses if there are no other (non-screening) documents missing.
  const canBypassDocumentsForScreenLast =
    isScreenLastStepCohort &&
    isRecruiterScreeningEnabled &&
    hasNotPassedScreening &&
    hasNotFailedScreening &&
    hasInterviewBookingFlow &&
    !hasNonScreeningDocumentsMissing;

  return {
    isEligibleForScreenLastScreeningModal,
    isEligibleForBookFirstConfirmation,
    canBypassDocumentsForScreenLast,
  };
}

export type BookingCohortEligibilityReturn = ReturnType<typeof useBookingCohortEligibility>;
